import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import Exporting from 'highcharts/modules/exporting'
import ExportData from 'highcharts/modules/export-data'
import Boost from 'highcharts/modules/boost'
import HighchartsReact from 'highcharts-react-official'

Exporting(Highcharts)
ExportData(Highcharts)
Boost(Highcharts)

export default function MeteogrfGraph(props) { 
  const { options, title, codes } = props;
  const { traceValue, clmValue, vrbValue, obsValue } = codes;
  const [ subtitle, setSubtitle ] = useState('Click and drag in the chart to zoom in.');

  // This is supposed to synchronize tooltips across charts, but isn't working
  /*  
  useEffect(() => {
    document.getElementById('container').addEventListener('mousemove', function(e) {
    var chart,
        point,
        i,
        event;

    for (i = 0; i < Highcharts.charts.length; i = i + 1) {
        chart = Highcharts.charts[i];
        var points = [];
        // Find coordinates within the chart
       if (chart) {
        event = chart.pointer.normalize(e);
        // Get the hovered point
        for(var j=0; j<chart.series.length; j++) {
          console.log(chart.series[j].searchPoint(event, true))
           point = chart.series[j].searchPoint(event, true);
           points.push(point);  
        }
console.log(points)
        //chart.tooltip.refresh(points);
       }

    }
      });       
  })

  Highcharts.Pointer.prototype.reset = function() { return undefined };
  Highcharts.Point.prototype.highlight = function (event) {
    event = this.series.chart.pointer.normalize(event);
    this.onMouseOver(); // Show the hover marker
    this.series.chart.tooltip.refresh(this); // Show the tooltip
    this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
};
*/  

  // Allow scatter plot values to appear in shared tooltip
  Highcharts.seriesTypes.scatter.prototype.noSharedTooltip = false;

  // Synchronize extremes (zooming) through the setExtremes event handler.
  function syncExtremes(e) {
    const thisChart = this.chart;
    if (e.trigger !== 'syncExtremes') { // Prevent feedback loop
      Highcharts.charts.forEach(chart => {
        if (chart && chart !== thisChart) {
          if (chart.xAxis[0].setExtremes) { // It is null while updating
            chart.xAxis[0].setExtremes(
              e.min,
              e.max,
              undefined,
              false,
              { trigger: 'syncExtremes' }
            );
          }
        }
      });
    }
  }

  // Resets chart zoom on selection event (removes all Reset Zoom buttons).
  function resetZoom(e) {
    // Prevent feedback loop
    if (e.resetSelection) { return; }
    // Zoom out all other charts on selection
    Highcharts.charts.forEach(chart => {
      if (chart && chart !== e.target) {
        chart.zoomOut();
      }
    });
  }
  // Any of these options can be overridden via props.options
  const localOptions = {
    accessibility: {
      enabled:false,
    },
    chart: {
      zooming: {
        type: 'x',
      },
      panning: true,
      panKey: 'shift',
      height: Math.max(200, Math.min(400, 800/options.length)),
      borderWidth: 1,
      borderColor: "lightgray",
      events: {
        selection: resetZoom,
      },
      otherChartsFollowAxes: true,
    },
    xAxis: { 
      type: "datetime",
      gridLineWidth: 1,
      crosshair: true,
      events: {
        setExtremes: syncExtremes,
        afterSetExtremes: function () {
          // reset instruction in subtitle
          setSubtitle(
            this.chart.resetZoomButton 
            ? 'Hold the shift key to pan the chart.'
            : 'Click and drag in the chart to zoom in.'
          )
        }
      }
    },
    tooltip: {
      shared: true,
      useHTML: true,
      hideDelay: 10,
      formatter: function() {
        let tooltipText = '<b>' + Highcharts.dateFormat("%e-%b-%Y %H:%M", this.x) + '</b><br/>';
        this.points.forEach(function (point) {
          let ptValue;
          if  (point.series.name.includes("Precipitation") && point.y === traceValue) {
            ptValue = "Trace";
          } else if (point.series.name.includes("Wind Direction") && point.y === clmValue) {
            ptValue = "Calm";
         } else if (point.series.name.includes("Wind Direction") && point.y === vrbValue) {
            ptValue = "Variable";
         } else if (point.series.name.includes("Cloud Cover")) {
            switch (point.y) {
              case 0:
                ptValue = "Clear";
                break;
              case 25:
                ptValue = "Few";
                break;
              case 50:
                ptValue = "Scattered";
                break;
              case 75:
                ptValue = "Broken";
                break;
              case 100:
                ptValue = "Overcast";
                break; 
              default:
                ptValue = point.y;        
            }
          } else {
            ptValue = point.y;
          }
          tooltipText += '<span style="color:' + point.color + '">\u25CF ' + point.series.name + ': </span>' + ptValue + '<br/>';
        });
        return tooltipText;
      },
    },
    credits: {
      href: "https://www.rcc-acis.org",
      text: ""
    },
    time: { 
      useUTC: true, //prevents time zone problems
    },
    legend: {
      borderWidth: 1,
      margin: 4,
      padding: 4,
    },
    title: {
      text: null,
    },
    yAxis: {
      title: {
        text: null
      },
      endOnTick: false,   // set in props options  
    },
    // More plotOptions may be provided via props options
    plotOptions: {
      column: {
          pointPlacement: 'on',
          //pointRange: 1,
      },
    },
    // Following must be provided via options.series in props
    series: null
  };

  //combine props option with default options
  const allOptions = []
  options.forEach((opts, i) => {
    allOptions.push({
      ...localOptions, ...opts,
      credits: { text: i === options.length-1 ? "Powered by ACIS" : ""},    //credits only below last graph
      plotOptions: { ...localOptions.plotOptions, ...opts.plotOptions },
    });
  })

  return (
    <div id="container">
      {options[0].series &&
        <>
          <Typography variant='h7' sx={{ fontWeight: 'bold', textAlign: 'center' }}>{title}</Typography>
          <Typography variant='body2' sx={{ fontStyle: 'italic', textAlign: 'center' }}>{subtitle}</Typography>
          {allOptions.map((opt, i) => (
            <HighchartsReact
              key={i}
              highcharts={Highcharts}
              options={opt}
            />
          ))}
        </>
      }
    </div>
  )
}